import cn from "classnames";
import { useTranslation } from "gatsby-plugin-react-i18next";
import parse from "html-react-parser";

import { useCountry } from "@func-core/core/application/hooks";
import { LandingFooterConfig } from "@func-core/core/domain/constants";
import { countries, CountryCode } from "@settings/countries";
import useVisibilityTracker from "@utils/hooks/useVisibilityTracker";
import { shouldShowEuFederLogos } from "@utils/locale-configuration-utils";

import TrackableLink from "@atoms/trackable-link/TrackableLink";
import ContactLinks from "@molecules/contact-links/ContactLinks";
import CountrySwitch from "@molecules/country-switch/CountrySwitch";
import SectionTitle from "@molecules/section-title/SectionTitle";

import logoSVG from "@images/logo/barkibu.svg";
import cdtiLogo from "@images/organisms/footer/cdti.png";
import DogMobile from "@images/organisms/footer/dog-mobile.svg";
import euLogo from "@images/organisms/footer/eu.png";
import facebookLogo from "@images/social/facebook.svg";
import instagramLogo from "@images/social/instagram.svg";
import twitterLogo from "@images/social/twitter.svg";

import * as styles from "@core/ui/organisms/footer/Footer.module.scss";

type SocialLogo = {
  src: string;
  width: string;
  height: string;
};

interface FooterProps {
  adoptionClassName?: string;
  isPreSales?: boolean;
  footerConfig: LandingFooterConfig;
}

const logos: Record<string, SocialLogo> = {
  facebook: {
    src: facebookLogo as string,
    width: "13px",
    height: "26px",
  },
  instagram: {
    src: instagramLogo as string,
    width: "26px",
    height: "26px",
  },
  twitter: {
    src: twitterLogo as string,
    width: "27px",
    height: "22px",
  },
};

const euFederLogos = [
  {
    src: euLogo as string,
    alt: "European Union",
    width: "69px",
    height: "58px",
  },
  {
    src: cdtiLogo as string,
    alt: "CDTI",
    width: "263px",
    height: "49px",
  },
];

const PreSalesFooter = ({
  adoptionClassName,
  isPreSales,
  footerConfig,
}: FooterProps): JSX.Element => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const { ref } = useVisibilityTracker("footer", {
    amount: 0.5,
  });

  const { socialNetworkLinks, resourceLinks, companyLinks } = footerConfig;

  return isPreSales ? (
    <footer ref={ref} className={cn("or-footer", styles.footer, adoptionClassName)}>
      <img
        src={logoSVG as string}
        alt={t("landing.footer.logo.alt")}
        width="111"
        height="24"
        loading={"lazy"}
      />
      <section className={styles.disclaimers}>
        <p>{parse(t("landing.footer.disclaimer.barkibu.text"))}</p>
        <p>{parse(t("landing.footer.disclaimer.google.text"))}</p>
        <p>{parse(t("landing.footer.disclaimer.copyright.text"))}</p>
      </section>
    </footer>
  ) : (
    <footer ref={ref} className={cn("or-footer", styles.footer, adoptionClassName)}>
      <section className={cn(styles.logoWrapper, socialNetworkLinks ? styles.withSocialLogos : "")}>
        <img
          src={logoSVG as string}
          alt={t("landing.footer.logo.alt")}
          width="111"
          height="24"
          loading={"lazy"}
        />

        {socialNetworkLinks && (
          <section className={styles.social}>
            <SectionTitle
              adoptionClassName={styles.sectionTitle}
              title={parse(t("contact.footer.social"))}
            />
            <ul className={styles.list}>
              {socialNetworkLinks.map((socialNetworkLink, idx) => {
                const logo = logos[socialNetworkLink.type.toLowerCase()];

                return (
                  <li key={idx}>
                    <TrackableLink to={socialNetworkLink.url}>
                      <img {...logo} alt={socialNetworkLink.type} loading={"lazy"} />
                    </TrackableLink>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </section>

      <section className={styles.contactWrapper}>
        <section className={styles.contactLinksWrapper}>
          <ContactLinks
            contactMethods={countries[country.iso.toUpperCase() as CountryCode].contactMethods}
            adoptionClassname={styles.contactLinks}
            trackingId="Footer"
          />

          <span className={styles.emailWrapper}>
            <span>{t("contact.footer.email.prefix")}</span>
            <TrackableLink
              to={`mailto:${t("contact.footer.email.destination")}`}
              openInNewWindow
              extraEventProperties={{
                destination: t("contact.footer.email.destination"),
                eventSender: "Footer - email",
              }}
            >
              {t("contact.footer.email.text")}
            </TrackableLink>
          </span>
        </section>
        <DogMobile />
      </section>

      {resourceLinks && (
        <section className={styles.resources}>
          <SectionTitle
            adoptionClassName={styles.sectionTitle}
            title={parse(t("contact.footer.resources"))}
          />

          <ul className={styles.list}>
            {resourceLinks.map((resourceLink, idx) => {
              return (
                <li key={idx}>
                  <TrackableLink
                    to={resourceLink.url}
                    openInNewWindow={true}
                    extraEventProperties={{
                      menuName: t("landing.footer.menu.name"),
                      linkName: t(resourceLink.text),
                    }}
                  >
                    {t(resourceLink.text)}
                  </TrackableLink>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {companyLinks && (
        <section className={styles.resources}>
          <SectionTitle
            adoptionClassName={styles.sectionTitle}
            title={parse(t("contact.footer.company"))}
          />

          <ul className={styles.list}>
            {companyLinks.map((companyLink, idx) => {
              return (
                <li key={idx}>
                  <TrackableLink
                    to={companyLink.url}
                    openInNewWindow={true}
                    extraEventProperties={{
                      menuName: t("landing.footer.menu.name"),
                      linkName: companyLink.text,
                    }}
                  >
                    {t(companyLink.text)}
                  </TrackableLink>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {shouldShowEuFederLogos(country.iso.toUpperCase()) && (
        <section className={styles.euFederLogos}>
          <ul className={styles.euFederLogosList}>
            {euFederLogos.map((logo, i) => {
              return (
                <TrackableLink
                  openInNewWindow
                  key={i}
                  to={"https://saludanimal.barkibu.com/cdti-feder"}
                  eventSender={"EU Feder Logos - Footer"}
                >
                  <li>
                    <img {...logo} alt={logo.alt} loading={"lazy"} />
                  </li>
                </TrackableLink>
              );
            })}
          </ul>
        </section>
      )}

      <section className={styles.countrySwitch}>
        <SectionTitle
          adoptionClassName={styles.sectionTitle}
          title={parse(t("contact.footer.country"))}
        />

        <CountrySwitch />
      </section>

      <section className={styles.disclaimers}>
        <p>{parse(t("landing.footer.disclaimer.barkibu.text"))}</p>
        <p>{parse(t("landing.footer.disclaimer.google.text"))}</p>
        <p>{parse(t("landing.footer.disclaimer.copyright.text"))}</p>
      </section>
    </footer>
  );
};

export default PreSalesFooter;
